import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { gtagPageView } from "../utilities";
import {
  enableBusinessWideReservation,
  enableProductTypeCategories,
  setFixedReservationDuration,
  setFixedReservationTimeFrame,
  uRentLogoVisibility,
} from "../features";
import { useMediaQuery, useTheme } from "@mui/material";
import { fi, enGB, sv } from "date-fns/locale";
import {
  isSameDay,
  isSameHour,
  isValid,
  add,
  format,
  formatISO,
  set,
  getDay,
  isToday,
  addHours,
  isBefore,
  isWithinInterval,
} from "date-fns";
import { translation } from "../components/Dictionary";
import { API, graphqlOperation } from "aws-amplify";
import { getReservationOffers } from "../graphql/client-queries";
import { captureError } from "../utilities";
import { selectAllCompanyIds } from "../features";
import services from "../shared/additional-services";
import { gtmAddVehicleToCart, gtmBeginCheckout, gtmViewItemList } from "../gtm";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { setGlobalLocalizationConfig } from "../shared/localizationConfig";
export const AppContext = createContext();
const timeFlexibility = 48 * 60 * 60 * 1000; //48h

export const AppProvider = ({ children }) => {
  const theme = useTheme();
  const mobileViewPort = useMediaQuery(theme.breakpoints.down("md"));
  const [activeStep, setActiveStep] = useState(1);
  const prevActiveStep = useRef();
  const [additionalServiceOffers, setAdditionalServiceOffers] = useState([]);
  const [cart, setCart] = useState([]);
  const [companies, setCompanies] = useState();
  const [notificationActive, setNotificationActive] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [reservationReturn, setReservationReturn] = useState();
  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState(
    []
  );

  const [selectedProduct, setSelectedProduct] = useState();
  const [reservationStart, setReservationStart] = useState();
  const [selectedCompany, setSelectedCompany] = useState();
  const [enableCart, setEnableCart] = useState(false);
  const [cartAlert, setCartAlert] = useState(false);
  const [cartAlertProduct, setCartAlertProduct] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [logoVisibility, setLogoVisibility] = useState(true);
  const [language, setLanguage] = useState("fi");
  const [dateHasError, setDateHasError] = useState(false);
  const [locale, setLocale] = useState(fi);
  const [offers, setOffers] = useState([]);
  const typeOfBusiness = useMemo(() => {
    if (selectedCompany?.organizationId) {
      return "organization";
    }
    return "legacy";
  }, [selectedCompany]);

  const [fetchOffersLoading, setFetchOffersLoading] = useState();

  // Determines if "Return to beginning" Button is shown
  const showBackToStartBtn = useMemo(() => {
    // Hide on cloud hosting to prevent Customers from seeing all Companys in the beginning.
    // At least until better logic (with limited visibility outside of embedded app) is implemented.
    const hiddenOrigin = process.env.REACT_APP_HOSTING_URL_CUSTOMER;
    if (hiddenOrigin === window.location.origin) return false;
    return true;
  }, []);

  const VUOKRAAPAKU_SPECIAL_COMPANY = useMemo(() => {
    const VUOKRAAPAKU_COMPANY_ID = "00ef9a27-c9ea-4947-9115-436433a83a35";
    return selectedCompany?.id === VUOKRAAPAKU_COMPANY_ID;
  }, [selectedCompany]);

  const groups = window.URENT_APP_GROUPS;
  // TODO: Obviously this works only as long as there is just one group on the embedd
  const enableCustomProviderLabel = () => {
    const group = groups?.[0];
    if (group) {
      const featureGroups = ["THERent", "gnista", "prgroup", "huuma"];
      const enabled = featureGroups.includes(group);
      if (enabled) {
        if (group === "gnista") {
          return translation.get(language, "Provider custom title service");
        } else {
          return translation.get(language, "Provider custom title product");
        }
      }
    }
    return false;
  };

  const cartTotalValue = useMemo(() => {
    let tempValues = [];
    let productsSum = cart.reduce(function (prev, current) {
      return prev + +current.offerPrice;
    }, 0);

    for (let item of selectedAdditionalServices) {
      let value = item.offerPrice;
      tempValues.push(value);
    }
    let additionalServiceSum = tempValues.reduce(function (prev, current) {
      return prev + +current;
    }, 0);
    let sum = additionalServiceSum + productsSum;
    return sum;
  }, [cart, selectedAdditionalServices]);

  // Check if cart is enabled
  useEffect(() => {
    if (selectedCompany) {
      setEnableCart(selectedCompany?.enableCart);
      if (
        setFixedReservationTimeFrame(selectedCompany?.group) &&
        activeStep !== 0
      ) {
        setActiveStep(0);
        setReservationStart();
        setReservationReturn();
      }
    }
  }, [selectedCompany, activeStep]);

  // handle uRent logo visibility
  useEffect(() => {
    if (companies) {
      extractAndCleanURL();
      setLogoVisibility(uRentLogoVisibility(companies[0]?.group));
    }
  }, [companies]);

  // Keeping track of the active step

  const handleActiveStep = (value) => {
    if (value === 1) {
      gtagPageView(
        "1. Valitse tuoteryhmä",
        "/1-valitse-tuoteryhma",
        "step-1-company-selected-done",
        selectedCompany?.enableGTM
      );
    }
    if (value === 2) {
      gtagPageView(
        "2. Valitse tuote",
        "/2-valitse-tuote",
        "step-2-products-selected-done",
        selectedCompany?.enableGTM
      );
    }
    if (value === 3) {
      gtagPageView(
        "3. Lisätarvikkeet",
        "/3-lisatarvikkeet",
        "step-3-services-selected-done",
        selectedCompany?.enableGTM
      );
    }
    if (value === 4) {
      gtmBeginCheckout(cart, selectedAdditionalServices, selectedCompany);
    }
    prevActiveStep.current = activeStep;
    setActiveStep(value);
  };

  // Controls of the Cart.js

  function addToCart(product) {
    // console.log("OR-46 addToCart");
    let checkCart = cart.find((c) => c.vehicle.id === product.vehicle?.id);

    if (checkCart && product.quantity > 0) {
      if (!product.quantity) return;
      let previousCart = cart
        .filter((item) => product.vehicle.id !== item.vehicle.id)
        .map((filteredCart) => {
          return filteredCart;
        });

      setCart([]);
      setCart([...previousCart, product]);
    }

    if (!checkCart) {
      setCart([...cart, product]);
      setCartAlertProduct(product.vehicle.name);
      setCartAlert(true);
      gtmAddVehicleToCart(product, selectedCompany);
    }
  }

  const closeCartAlert = () => {
    setCartAlert(false);
  };

  const handleClearCart = () => {
    setCart([]);
    setCartAlert(false);
  };

  // Remove product from the cart
  const deleteProduct = (item) => {
    let previousCart = cart
      .filter((product) => product.vehicle.id !== item.vehicle.id)
      .map((filteredCart) => {
        return filteredCart;
      });
    setCart([]);
    setCart(previousCart);
    setCartAlert(false);
    setCartAlertProduct();
    // if there is no items in the cart, customer is redirected to the step 1, WHY?
    /*     if (previousCart.length === 0) {
      setCart([]);
      setIsCartOpen(false);
      setActiveStep(1);
    } */
  };

  // This should not be in use, remove when verified
  const handleCartTotalValue = (value) => {
    console.log("Removed method");
  };

  // Handling selected companies

  const handleSelectedCompany = (company) => {
    if (!company) return;
    setCart([]);
    setOffers([]);
    setSelectedCompany(company);
    // handle situation where customer switches between companies and one of the companies has feature disableSameDay enabled.

    if (company?.disableSameDay) {
      if (isSameDay(new Date(), selectedDateRange[0])) {
        setSelectedDateRange([null, null]);
      }
    }

    setInitSearch(true);
  };

  useEffect(() => {
    if (selectedCompany?.currency) {
      console.info(
        "Using a non default currency",
        { currency: selectedCompany.currency },
        { locale: selectedCompany.locale }
      );
      setGlobalLocalizationConfig({
        currency: selectedCompany.currency,
        locale: selectedCompany.locale,
      });
    }
  }, [selectedCompany]);

  const handleCompanies = (company) => {
    setCart([]);
    setCompanies(company);
  };

  // true = use bundled categories by product type
  // false = use default categories
  const FEATURE_USE_PRODUCT_TYPE_CATEGORIES = enableProductTypeCategories(
    selectedCompany?.organizationId
  );

  // enable feature forced service. One of the two determined additional services is always selected.
  // the initial service is set in AdditionalServices components fetch function
  const FEATURE_FORCED_SERVICE_IDS = [
    "8f036a86-2734-4b90-9548-9ca5d7e04b16",
    "f5b34ae1-b31b-44c2-9c07-fe503c6f0967",
  ];

  const FEATURE_FORCED_SERVICE = useMemo(() => {
    if (selectedCompany?.id === "bd1ac06b-fc86-470f-8a85-9d3a07d7f3b1") {
      // check if both services are still available

      const includesBothServices =
        additionalServiceOffers.some((s) =>
          s.id.includes(FEATURE_FORCED_SERVICE_IDS[0])
        ) &&
        additionalServiceOffers.some((s) =>
          s.id.includes(FEATURE_FORCED_SERVICE_IDS[1])
        );

      if (includesBothServices) return true;

      return false;
    }
  }, [selectedCompany, additionalServiceOffers]);

  const switchForcedService = (service) => {
    const otherServiceId = FEATURE_FORCED_SERVICE_IDS.filter(
      (id) => id !== service.id
    );

    if (selectedAdditionalServices.find((s) => s.id === service.id)) {
      const services = selectedAdditionalServices.filter(
        (s) => s.id !== service.id
      );

      const newService = {
        ...additionalServiceOffers.find((s) => s.id === otherServiceId[0]),
        quantity: 1,
      };

      setSelectedAdditionalServices(services);

      services.push(newService);
    } else {
      const services = selectedAdditionalServices.filter(
        (s) => s.id !== otherServiceId[0]
      );

      const newService = {
        ...additionalServiceOffers.find((s) => s.id === service.id),
        quantity: 1,
      };

      services.push(newService);
      setSelectedAdditionalServices(services);
    }
  };

  // Remove additional service from cart
  const deleteService = (service) => {
    const newArrayofServices = selectedAdditionalServices.filter(
      (item) => item.id !== service
    );

    setSelectedAdditionalServices(newArrayofServices);
  };

  const convertUnixTimestampToDate = (unixTimestamp) => {
    return new Date(unixTimestamp * 1000); // Convert to milliseconds
  };

  const extractAndCleanURL = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const params = {
      PickUpLocationCode: queryParams.get("PickUpLocationCode"),
      PickUpTimestamp: queryParams.get("PickUpTimestamp"),
      ReturnTimestamp: queryParams.get("ReturnTimestamp"),
      lang: queryParams.get("lang"),
    };

    // Check if lang is valid and set it to state
    if (["fi", "se", "en"].includes(params.lang)) {
      setLanguage(params.lang);
    }

    const filteredCompany = companies.filter(
      (company) => company.id === params.PickUpLocationCode
    );

    if (filteredCompany) setSelectedCompany(filteredCompany[0]);

    const pickUpDate = convertUnixTimestampToDate(
      parseInt(params.PickUpTimestamp, 10)
    );
    const returnDate = convertUnixTimestampToDate(
      parseInt(params.ReturnTimestamp, 10)
    );

    // Construct selectedDateRange array with potential null values
    const selectedDateRange = [
      params.PickUpTimestamp ? pickUpDate : null,
      params.ReturnTimestamp ? returnDate : null,
    ];

    setSelectedDateRange(selectedDateRange);

    // Clear the URL parameters from the address bar
    // TODO: CU-861n87y9z
    if (window.location.origin === "https://vuokraus.urent.fi") {
      window.history.replaceState({}, document.title, window.location.origin);
    }

    if (
      params.PickUpLocationCode &&
      params.PickUpTimestamp &&
      params.ReturnTimestamp
    ) {
      if (!initSearch) setInitSearch(true);
    }
  };

  // Remove additional services from the cart when going back a step to change the main product.

  // Control of selected category

  // Control of selected main product

  const handleSelectedProduct = (product) => {
    setSelectedProduct(product);
  };

  // Selected time variables

  // Control of notifications and messages

  const replaceCart = (newCart) => {
    setCart(newCart);
  };

  const setNotification = (message) => {
    setNotificationMessage(message);
    setNotificationActive(true);
  };

  const hideNotification = () => {
    setNotificationActive(false);
  };

  useEffect(() => {
    if (activeStep < 2) {
      setSelectedAdditionalServices([]);
    }
    if (activeStep <= 1) {
      handleClearCart();
    }
  }, [activeStep]);

  useEffect(() => {
    if (cartAlert === true) {
      closeCartAlert();
    }
  }, [activeStep]);

  useEffect(() => {
    if (language === "fi") {
      setLocale(fi);
    }
    if (language === "en") {
      setLocale(enGB);
    }
    if (language === "se") {
      setLocale(sv);
    }
  }, [language]);

  // RESERVATION DATE
  const [initSearch, setInitSearch] = useState(false);
  const [selectedPicker, setSelectedPicker] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [fixedDate, setFixedDate] = useState(null);
  const [dateRange, setDateRange] = useState({ from: null, to: null }); // Used if multiday reservation enabled (default)

  // customer can determine a starting point of the reservation and the end date is fixed to the given timespan
  // No time selection for the reservation.
  const FEATURE_FIXED_RESERVATION_DURATION = useMemo(() => {
    return setFixedReservationDuration(selectedCompany?.id, companies);
  }, [selectedCompany, companies]);

  // start date & time defines the value of the end date & time by the values in fixedTimeFrames array.
  // customer can select end time within that array.
  const FEATURE_FIXED_RESERVATION_TIMEFRAME = useMemo(() => {
    if (!selectedCompany) return;
    return setFixedReservationTimeFrame(selectedCompany?.id);
  }, [selectedCompany]);

  // Start & return time selects are hidden, and set to 23:59 && 00:00
  const FEATURE_NO_TIME_SELECT = useMemo(() => {
    const featureAccessIds = [
      "0f4b7225-f433-45c7-a08a-2899562e5916", // THERent
      "9d2669e4-df78-11ee-bd3d-0242ac120002", // Roberts
      "c40e757a-dd47-11ee-bd3d-0242ac120002", // Roberts
    ];

    return featureAccessIds.includes(selectedCompany?.id);
  }, [selectedCompany]);

  const getOrderNum = (reservation) => {
    return !!reservation.vehicle.orderNumber
      ? reservation.vehicle.orderNumber
      : 999999;
  };
  const [searchButtonError, setSearchButtonError] = useState(false);

  const enableVehicleStartReturnTimes = useMemo(() => {
    if (selectedCompany?.enableVehicleStartReturnTimes) return selectedCompany;
    return null;
  }, [selectedCompany]);

  const disableMultiday = useMemo(() => {
    if (
      selectedCompany?.disableMultiday ||
      FEATURE_FIXED_RESERVATION_TIMEFRAME ||
      FEATURE_FIXED_RESERVATION_DURATION
    )
      return !!selectedCompany;
    return null;
  }, [selectedCompany, FEATURE_FIXED_RESERVATION_TIMEFRAME]);

  // Business hours in date form for start and end days
  const businessHourDates = (selectedDateRange, selectedCompany) => {
    if (!selectedDateRange[0]) return;
    const startAt = startDayBusinessHours(
      selectedCompany,
      selectedDateRange[0]
    )?.openAt;
    const closeAt = startDayBusinessHours(
      selectedCompany,
      selectedDateRange[0]
    )?.closeAt;
    if (startAt && closeAt) {
      // DateTimes based of starting day business hours
      const start = businessHourToDate(startAt, closeAt, selectedDateRange[0]);
      if (selectedDateRange[1]) {
        const endStartAt = endDayBusinessHours(
          selectedDateRange,
          selectedCompany
        )?.openAt;
        const endCloseAt = endDayBusinessHours(
          selectedDateRange,
          selectedCompany
        )?.closeAt;

        if (endStartAt && endCloseAt) {
          // DateTimes based of ending day business hours
          const end = businessHourToDate(
            endStartAt,
            endCloseAt,
            selectedDateRange[1]
          );
          return {
            start: start,
            end: end,
          };
        }
      }
      // start and end are the same
      return {
        start: start,
        end: start,
      };
    }
    // Business hours not set for start or close
    return null;
  };

  const endDayBusinessHours = (selectedDateRange, selectedCompany) => {
    if (isValid(selectedDateRange[0])) {
      const date = isValid(selectedDateRange[1])
        ? selectedDateRange[1]
        : selectedDateRange[0];

      const businessHours = selectedCompany?.weeklyBusinessHours;
      return businessHours[getDay(date)];
    }
  };

  // Dates for easier comparison of times
  const businessHourToDate = (startAt, closeAt, date) => {
    const openingHours = parseInt(startAt.split(":")[0]);
    const openingMinutes = parseInt(startAt.split(":")[1]);
    const openingTime = set(date, {
      hours: openingHours,
      minutes: openingMinutes,
    });
    const closingHours = parseInt(closeAt.split(":")[0]);
    const closingMinutes = parseInt(closeAt.split(":")[1]);
    const closingTime = set(date, {
      hours: closingHours,
      minutes: closingMinutes,
    });
    return { openingTime: openingTime, closingTime: closingTime };
  };

  const startDayBusinessHours = (selectedCompany, date) => {
    const businessHours = selectedCompany?.weeklyBusinessHours;
    if (!businessHours) return;
    const dateStr = isValid(date)
      ? formatISO(date, { representation: "date" })
      : null;
    const businessHoursExceptions =
      selectedCompany.businessHoursExceptions.items;
    const exception = businessHoursExceptions.find(
      (bhe) => bhe.date === dateStr
    );
    if (exception?.businessHours) return exception.businessHours;
    return businessHours[getDay(date)];
  };

  const startDateString = useMemo(() => {
    return isValid(selectedDateRange[0])
      ? format(selectedDateRange[0], "eeeeee dd.MM", {
          locale: locale,
        })
      : "-";
  }, [selectedDateRange, locale]);

  const endDateString = useMemo(() => {
    return isValid(selectedDateRange[1])
      ? format(selectedDateRange[1], "eeeeee dd.MM", {
          locale: locale,
        })
      : "-";
  }, [selectedDateRange, locale]);

  const fromDate = useMemo(() => {
    if (!dateRange.from) return null;
    return new Date(
      dateRange.from.year,
      dateRange.from.month - 1,
      dateRange.from.day
    );
  }, [dateRange]);

  const toDate = useMemo(() => {
    return dateRange.to
      ? new Date(dateRange.to.year, dateRange.to.month - 1, dateRange.to.day)
      : fromDate;
  }, [dateRange, fromDate]);

  const useSpecificPickupReturnCompany = useMemo(() => {
    // Specific feature for this one Company, hence hardcoded for now.
    if (VUOKRAAPAKU_SPECIAL_COMPANY) {
      const hardCodedTimes = {
        pickupAt: ["10:30", "16:30"],
        returnAt: ["10:00", "16:00"],
      };
      // Sunday indexed as 0
      const hardCodedWeekEntries = [
        hardCodedTimes,
        null,
        null,
        null,
        null,
        null,
        hardCodedTimes,
      ];
      const weeklyPickupReturnTimes = hardCodedWeekEntries;
      return { ...selectedCompany, weeklyPickupReturnTimes };
    }
  }, [selectedCompany, VUOKRAAPAKU_SPECIAL_COMPANY]);
  const disableOffHourReturnCompany = useMemo(() => {
    if (selectedCompany?.disableOffHourReturn) return selectedCompany;
    return null;
  }, [selectedCompany]);

  // See if time is in times
  const isAllowedTime = (time, allowedTimes) => {
    const hours = time.getHours();
    // Get minutes in double digit form. Eg. 00 or 30
    const minutes = ("0" + time.getMinutes()).slice(-2);
    const timeStr = hours + ":" + minutes;
    if (!allowedTimes.some((time) => time === timeStr)) {
      return false;
    }
    return true;
  };

  // See if there are errors related to chosen pickup / return times
  const endTimeOk = useMemo(() => {
    const formatDayTimeString = (date) => {
      return format(new Date(date), "eeeeee HH:mm", {
        locale: locale,
      });
    };

    const getSpecificFromToTimes = (date) => {
      if (!useSpecificPickupReturnCompany) return null;
      return useSpecificPickupReturnCompany.weeklyPickupReturnTimes[
        getDay(date)
      ];
    };

    const nextOpeningDatetime = (toDate) => {
      // Find next available return time withing a week
      let nextReturnDate;
      let i = 1;
      const businessHoursExceptions =
        selectedCompany?.businessHoursExceptions?.items;
      while (i <= 7) {
        const date = add(toDate, { days: i });
        // If Company is closed by exception, move onto next day
        const dateStr = formatISO(date, { representation: "date" });
        const exception = businessHoursExceptions.find(
          (bhe) => bhe.date === dateStr
        );
        if (exception?.businessHours === null) {
          i = i + 1;
          continue;
        }
        if (useSpecificPickupReturnCompany) {
          const allowedTimes =
            useSpecificPickupReturnCompany.weeklyPickupReturnTimes[
              getDay(date)
            ];
          if (allowedTimes?.returnAt) {
            nextReturnDate = set(date, {
              hours: allowedTimes.returnAt[0].split(":")[0], // times are in hh:mm format
              minutes: allowedTimes.returnAt[0].split(":")[1],
            });
            return nextReturnDate;
          }
        }
        if (disableOffHourReturnCompany) {
          if (exception?.businessHours) {
            return set(date, {
              hours: exception.businessHours.openAt.split(":")[0], // times are in hh:mm format
              minutes: exception.businessHours.openAt.split(":")[1],
            });
          }
          const weeklyBusinessHours =
            disableOffHourReturnCompany?.weeklyBusinessHours;
          const businessHours = weeklyBusinessHours[getDay(date)];
          if (!businessHours) {
            i = i + 1;
            continue;
          }
          return set(date, {
            hours: businessHours.openAt.split(":")[0], // times are in hh:mm format
            minutes: businessHours.openAt.split(":")[1],
          });
        }
        i = i + 1;
      }
    };

    if (!selectedCompany) return { hasError: false };
    if (dateRange.from) {
      if (!startDayBusinessHours(selectedCompany, selectedDateRange[0])) {
        //  setSelectedPicker("calendar");
        return {
          hasError: true,
          message: `${translation.get(
            language,
            "Daterange error: closed"
          )} ${startDateString}`,
        };
      }
      if (
        !endDayBusinessHours(selectedDateRange, selectedCompany) &&
        disableOffHourReturnCompany
      ) {
        //   setSelectedPicker("calendar");
        return {
          hasError: true,
          message: `${translation.get(
            language,
            "Daterange error: closed"
          )} ${endDateString}`,
        };
      }
    }
    let specificToTimes;
    if (fromDate) {
      // See if there are specific from/to times set that overrides businessHours
      const specificFromTimes = getSpecificFromToTimes(fromDate);
      if (specificFromTimes) {
        // Pickup and return  times must match exactly the pre-determined times
        if (!isAllowedTime(selectedDateRange[0], specificFromTimes.pickupAt)) {
          // TODO:

          return {
            hasError: true,
            message: `${translation.get(
              language,
              "Daterange allowed start times"
            )} ${startDateString} ${specificFromTimes.pickupAt}`,
          };
        }
      }
      specificToTimes = getSpecificFromToTimes(toDate);
      if (specificToTimes) {
        if (
          specificToTimes &&
          !isAllowedTime(selectedDateRange[1], specificToTimes.returnAt)
        ) {
          const nextReturnDate = nextOpeningDatetime(toDate);
          const nextDateString = formatDayTimeString(nextReturnDate);
          const message = disableMultiday
            ? `${translation.get(
                language,
                "Daterange allowed return times"
              )} ${endDateString} ${specificToTimes.returnAt}`
            : `${translation.get(
                language,
                "Daterange allowed return times"
              )} ${endDateString} ${
                specificToTimes.returnAt
              }. Voit valita palautuksen myös tulevalle päivälle, seuraava mahdollinen ${nextDateString}`;
          return {
            hasError: true,
            message: message,
          };
        }
      }

      const deliveryBufferHours = selectedCompany?.deliveryBufferHours;
      if (deliveryBufferHours) {
        if (deliveryBufferHours && isToday(selectedDateRange[0])) {
          if (
            addHours(new Date(), deliveryBufferHours) > selectedDateRange[0]
          ) {
            return {
              hasError: true,
              errorComponent: "startTime",
              message: `Aloitusaika täytyy olla ${deliveryBufferHours} nykyhetkestä.`,
            };
          }
        } else {
          const denyTime = isWithinInterval(selectedDateRange[0], {
            start: new Date(),
            end: addHours(new Date(), deliveryBufferHours),
          });
          if (denyTime) {
            return {
              hasError: true,
              errorComponent: "startTime",
              message: `Aloitusaika täytyy olla ${deliveryBufferHours} tuntia nykyhetkestä.`,
            };
          }
        }
      }

      // startTime should not be before opening time
      if (!enableVehicleStartReturnTimes) {
        const startDayOpenTime = businessHourDates(
          selectedDateRange,
          selectedCompany
        )?.start?.openingTime;
        const startDayCloseTime = businessHourDates(
          selectedDateRange,
          selectedCompany
        )?.start?.closingTime;

        if (!specificFromTimes && selectedDateRange[0] < startDayOpenTime) {
          return {
            hasError: true,
            errorComponent: "startTime",
            message: `${startDateString} ${translation.get(
              language,
              "Daterange opening time"
            )} ${
              startDayBusinessHours(selectedCompany, selectedDateRange[0])
                ?.openAt
            }`,
          };
        }
        if (!specificFromTimes && selectedDateRange[0] > startDayCloseTime) {
          return {
            hasError: true,
            errorComponent: "startTime",
            message: `${startDateString} ${translation.get(
              language,
              "Daterange closing time"
            )}  ${
              startDayBusinessHours(selectedCompany, selectedDateRange[0])
                ?.closeAt
            }`,
          };
        }
      }

      if (disableOffHourReturnCompany) {
        // endTime can't be outside business hours, if disableOffHourReturn is set to true
        const endDayOpenTime = businessHourDates(
          selectedDateRange,
          selectedCompany
        )?.end?.openingTime;
        const endDayCloseTime = businessHourDates(
          selectedDateRange,
          selectedCompany
        )?.end?.closingTime;
        // See if endTime is outside the businessHours on the given day, unless specificToTimes are set.
        // In that case we always want to show just the allowed times
        if (selectedDateRange[1] > endDayCloseTime && !specificToTimes) {
          const nextReturnDate = nextOpeningDatetime(toDate);
          const nextDateString = formatDayTimeString(nextReturnDate);
          const message = disableMultiday
            ? `${endDateString} ${translation.get(
                language,
                "Daterange closing time"
              )} ${
                endDayBusinessHours(selectedDateRange, selectedCompany)?.closeAt
              }.`
            : `${endDateString} ${translation.get(
                language,
                "Daterange closing time"
              )} ${
                endDayBusinessHours(selectedDateRange, selectedCompany)?.closeAt
              }.
            ${translation.get(
              language,
              "Daterange next day time"
            )} ${nextDateString}`;
          return {
            hasError: true,
            message: message,
            errorComponent: "returnTime",
          };
        }
        if (selectedDateRange[1] < endDayOpenTime && !specificToTimes) {
          return {
            hasError: true,
            errorComponent: "returnTime",
            message: `${endDateString} ${translation.get(
              language,
              "Daterange opening time"
            )} ${
              endDayBusinessHours(selectedDateRange, selectedCompany)?.openAt
            }`,
          };
        }
      }
      if (isBefore(selectedDateRange[1], selectedDateRange[0])) {
        return {
          hasError: true,
          errorComponent: "returnTime",
          message: `Palautuspäivä ei voi olla ennen aloituspäivää.`,
        };
      }
    }

    return { hasError: false };
  }, [
    locale,
    useSpecificPickupReturnCompany,
    selectedDateRange,
    startDateString,
    dateRange.from,
    selectedCompany,
    disableOffHourReturnCompany,
    disableMultiday,
    enableVehicleStartReturnTimes,
    endDateString,
    fromDate,
    toDate,
    language,
    businessHourDates,
  ]);

  const hasError = useMemo(() => {
    // TODO: check fixed time frame different way maybe?
    if (
      ((disableMultiday && FEATURE_FIXED_RESERVATION_TIMEFRAME) ||
        (isValid(selectedDateRange[0]) &&
          isSameDay(selectedDateRange[0], selectedDateRange[1]))) &&
      selectedDateRange[0] >= selectedDateRange[1]
    ) {
      return true;
    }
    if (endTimeOk.hasError) {
      return true;
    }
    return false;
  }, [
    FEATURE_FIXED_RESERVATION_TIMEFRAME,
    selectedDateRange,
    disableMultiday,
    endTimeOk,
  ]);

  useEffect(() => {
    setSearchButtonError(hasError);
  }, [hasError]);
  // Temp fix to prevent crashing when disableMultiday is changed
  useEffect(() => {
    if (!Array.isArray(selectedDateRange) && disableMultiday) {
      setSelectedDateRange([reservationStart, reservationReturn]);
    }
  }, [disableMultiday, reservationReturn, reservationStart, selectedDateRange]);

  // Add error state to AppContext, this is used when company is changed
  useEffect(() => {
    setDateHasError(endTimeOk.hasError);
  }, [endTimeOk.hasError, setDateHasError]);

  useEffect(() => {
    const fromDate = isValid(selectedDateRange[0])
      ? {
          year: selectedDateRange[0].getFullYear(),
          month: selectedDateRange[0].getMonth() + 1,
          day: selectedDateRange[0].getDate(),
        }
      : null;

    const toDate = isValid(selectedDateRange[1])
      ? {
          year: selectedDateRange[1].getFullYear(),
          month: selectedDateRange[1].getMonth() + 1,
          day: selectedDateRange[1].getDate(),
        }
      : null;

    setDateRange({ from: fromDate, to: toDate });
  }, [selectedDateRange]);

  function search() {
    if (
      isValid(selectedDateRange[0]) &&
      isValid(selectedDateRange[1]) &&
      selectedCompany &&
      !endTimeOk.hasError
    ) {
      setReservationStart(selectedDateRange[0]);
      setReservationReturn(selectedDateRange[1]);

      // remove items from the cart when new search
      handleClearCart();
      // Retrieve offers when timespan has been selected from DateRange
      setFetchOffersLoading(true);

      onSubmit(
        selectedDateRange[0],
        selectedDateRange[1],
        companies,
        selectedCompany,
        getOrderNum
      ).then((response) => {
        if (response) {
          if (response.length === 0) {
            setOffers("no offers");
          } else {
            setOffers(response);
          }

          setFetchOffersLoading(false);
        }
      });
      handleActiveStep(2);
    }
  }

  useEffect(() => {
    if (initSearch) {
      setSelectedAdditionalServices([]);
      setAdditionalServiceOffers([]);
      setOffers([]);
      search();
    }

    return () => {
      setInitSearch(false);
    };
  }, [initSearch]);

  const store = {
    typeOfBusiness,
    activeStep,
    groups,
    additionalServiceOffers,
    addToCart,
    cart,
    cartTotalValue,
    companies,
    enableCart,
    handleActiveStep,
    enableCustomProviderLabel,
    setAdditionalServiceOffers,
    handleCartTotalValue,
    handleClearCart,
    handleCompanies,
    deleteProduct,
    closeCartAlert,
    setIsCartOpen,
    handleSelectedCompany,
    handleSelectedProduct,
    hideNotification,
    isCartOpen,
    logoVisibility,
    notificationActive,
    notificationMessage,
    cartAlert,
    cartAlertProduct,
    selectedAdditionalServices,
    selectedCompany,
    selectedProduct,
    language,
    locale,
    setLanguage,
    setReservationReturn,
    setReservationStart,
    setNotification,
    setSelectedAdditionalServices,
    setSelectedCompany,
    reservationStart,
    reservationReturn,
    deleteService,
    replaceCart,
    mobileViewPort,
    prevActiveStep,
    dateHasError,
    setDateHasError,
    offers,
    setOffers,
    fetchOffersLoading,
    setFetchOffersLoading,
    searchButtonError,
    selectedDateRange,
    setSelectedDateRange,
    endTimeOk,
    hasError,
    initSearch,
    setFixedDate,
    businessHourDates,
    fixedDate,
    FEATURE_FIXED_RESERVATION_TIMEFRAME,
    FEATURE_FIXED_RESERVATION_DURATION,
    FEATURE_NO_TIME_SELECT,
    FEATURE_USE_PRODUCT_TYPE_CATEGORIES,
    setSelectedPicker,
    selectedPicker,
    useSpecificPickupReturnCompany,
    endDateString,
    startDateString,
    dateRange,
    disableMultiday,
    setSearchButtonError,
    setInitSearch,
    showBackToStartBtn,
    FEATURE_FORCED_SERVICE,
    FEATURE_FORCED_SERVICE_IDS,
    switchForcedService,
  };

  return <AppContext.Provider value={store}>{children}</AppContext.Provider>;
};

const fetchGetReservationOffers = async (
  companyIds,
  startTime,
  returnTime,
  selectedCompany
) => {
  const { group, orgBusinessId, organizationId } = selectedCompany;
  let startCompanyId;
  if (enableBusinessWideReservation(selectedCompany)) {
    startCompanyId = companyIds[0];
  }
  const response = await API.graphql(
    graphqlOperation(getReservationOffers, {
      companyIds: startCompanyId ? undefined : companyIds,
      startCompanyId: startCompanyId,
      group: group ?? undefined,
      orgBusinessId: orgBusinessId ?? undefined,
      startTime: zonedTimeToUtc(startTime, selectedCompany.timezone).toJSON(),
      returnTime: zonedTimeToUtc(returnTime, selectedCompany.timezone).toJSON(),
      timeFlexibility,
    })
  );
  const reservationOffers = response.data.getReservationOffers.map((r) => ({
    ...r,
    startTime: utcToZonedTime(new Date(r.startTime), selectedCompany.timezone),
    returnTime: utcToZonedTime(
      new Date(r.returnTime),
      selectedCompany.timezone
    ),
    freeTimes: r.freeTimes.map((f) => ({
      firstStartTime: utcToZonedTime(
        new Date(f.firstStartTime),
        selectedCompany.timezone
      ),
      lastStartTime: utcToZonedTime(
        new Date(f.lastStartTime),
        selectedCompany.timezone
      ),
      lastReturnTime: utcToZonedTime(
        new Date(f.lastReturnTime),
        selectedCompany.timezone
      ),
      returnTimes: f.returnTimes.map((rt) => ({
        start: utcToZonedTime(new Date(rt.start), selectedCompany.timezone),
        end: utcToZonedTime(new Date(rt.end), selectedCompany.timezone),
      })),
    })),
  }));
  gtmViewItemList(reservationOffers, startTime, returnTime, selectedCompany);
  return reservationOffers;
};

const onSubmit = async (
  reservationStart,
  reservationReturn,
  companies,
  selectedCompany,
  getOrderNum
) => {
  const FEATURE_SELECT_ALL_COMPANY_IDS = selectAllCompanyIds(companies);

  if (
    !reservationStart ||
    !reservationReturn ||
    reservationStart >= reservationReturn
  ) {
    console.error("haku virhe", reservationStart, reservationReturn);
    // This might not stop getReservationOffers from running, but it hides
    // wrongful results from the user
  } else {
    try {
      let companyIds = [selectedCompany.id];
      if (FEATURE_SELECT_ALL_COMPANY_IDS) {
        // If select all feature is in use, get correct array of Company ids to be used
        if (selectedCompany.id === "all") {
          companyIds = FEATURE_SELECT_ALL_COMPANY_IDS;
        }
      }
      const reservationOffers = await fetchGetReservationOffers(
        companyIds,
        reservationStart,
        reservationReturn,
        selectedCompany
      );
      const getTimeDiff = (resolvedTime) => {
        return Math.abs(resolvedTime.getTime() - reservationStart.getTime());
      };
      const sortedOffers = reservationOffers.sort((a, b) => {
        if (getTimeDiff(a.startTime) !== 0 || getTimeDiff(b.startTime) !== 0) {
          return getTimeDiff(a.startTime) - getTimeDiff(b.startTime);
        }
        return getOrderNum(a) - getOrderNum(b);
      });

      return sortedOffers;
    } catch (e) {
      captureError("Get reservation offers failed", "GET_OFFERS_FAILED", e);
      console.log("error", e);
      return "error";
    }
  }
};
